// MARK://dhk8/admin-shell/pages/modules/DevicesPage
// Módulo 4 — Estaciones de impresión → dhk8-pos-printing.
//
// MODELO REAL (verificado en services/pos-printing): una estación lógica
// `PosPrintStation` (nombre, sucursal, orden + cloud-pairing) tiene N SALIDAS
// `PosPrintStationOutput` (KDS o impresora). Las impresoras FÍSICAS las posee el
// Bridge; el backend solo conoce estaciones + salidas + el pairing cloud (código
// de emparejamiento, lastSeenAt → online si < 20s).
//
// Lecturas (REST, une /v1/printing/stations + /v1/print/relay/stations) y
// escrituras (estación + salidas) viven en lib/clients/print-stations-rest.ts.
// El dropdown Catálogo→Categorías sigue leyendo por el grafo (print-stations.ts).
//
// DS NintaiGO: DataTable + StatusPill + SectionedSheet + useConfirm + useToast.
// Curvas, sin border-left, clases adm-*/nui-*.
import { useMemo, useState } from 'react'
import { Plus, Pencil, Trash2, Sliders, Radio, Link2 } from 'lucide-react'
import * as ps from '../../lib/clients/print-stations-rest.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import DataTable, { type DataTableColumn } from '../../components/DataTable.js'
import StatusPill, { type StatusTone } from '../../components/StatusPill.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import { useToast } from '../../components/Toast.js'

// ── helpers ────────────────────────────────────────────────────────────────
const ONLINE_WINDOW_MS = 20_000 // online = ahora − lastSeenAt < 20s

function pairingStatus(s: ps.Station): { tone: StatusTone; label: string } {
  if (!s.lastSeenAt) return { tone: 'neutral', label: 'Sin emparejar' }
  const fresh = Date.now() - new Date(s.lastSeenAt).getTime() < ONLINE_WINDOW_MS
  // Respetamos también la bandera online del backend (calculada igual, 20s).
  return s.online || fresh
    ? { tone: 'ok', label: 'En línea' }
    : { tone: 'danger', label: 'Sin conexión' }
}

const OUTPUT_TYPE_LABEL: Record<string, string> = { KDS: 'KDS (pantalla)', PRINTER: 'Impresora' }
const PAPER_LABEL: Record<string, string> = { '58mm': '58 mm', '80mm': '80 mm', A4: 'A4' }
const TEMPLATE_LABEL: Record<string, string> = {
  'KITCHEN-thermal': 'Comanda (cocina)',
  'RECEIPT-thermal': 'Ticket (recibo)',
  'SLIP-thermal': 'Vale (slip)',
}

// ── Station form ─────────────────────────────────────────────────────────────
interface StationForm { name: string; sortOrder: string; branchId: string }
const emptyStationForm = (): StationForm => ({ name: '', sortOrder: '0', branchId: '' })

// ── Output form ──────────────────────────────────────────────────────────────
interface OutputForm {
  type: ps.OutputType
  name: string
  paperWidth: ps.PaperWidth
  templateKind: ps.TemplateKind
  enabled: boolean
}
const emptyOutputForm = (): OutputForm => ({
  type: 'PRINTER', name: '', paperWidth: '80mm', templateKind: 'KITCHEN-thermal', enabled: true,
})

export default function DevicesPage() {
  const confirm = useConfirm()
  const { toast } = useToast()
  const list = useAsync((s) => ps.listStationsFull(s))

  // Estación: crear / editar
  const [stationSheet, setStationSheet] = useState<null | { mode: 'create' } | { mode: 'edit'; station: ps.Station }>(null)
  const [stationForm, setStationForm] = useState<StationForm>(emptyStationForm())
  const [stationErr, setStationErr] = useState<string | null>(null)
  const [savingStation, setSavingStation] = useState(false)

  // Salidas: gestión por estación (id de la estación abierta)
  const [outputsForId, setOutputsForId] = useState<string | null>(null)
  const outputsStation = useMemo(
    () => list.data?.find((s) => s.id === outputsForId) ?? null,
    [list.data, outputsForId],
  )

  // Salida: crear / editar (dentro de la estación abierta)
  const [outputSheet, setOutputSheet] = useState<null | { mode: 'create' } | { mode: 'edit'; output: ps.StationOutput }>(null)
  const [outputForm, setOutputForm] = useState<OutputForm>(emptyOutputForm())
  const [outputErr, setOutputErr] = useState<string | null>(null)
  const [savingOutput, setSavingOutput] = useState(false)

  // ── estación: handlers ─────────────────────────────────────────────────────
  function openCreateStation() {
    setStationForm(emptyStationForm())
    setStationErr(null)
    setStationSheet({ mode: 'create' })
  }
  function openEditStation(station: ps.Station) {
    setStationForm({ name: station.name, sortOrder: String(station.sortOrder), branchId: station.branchId ?? '' })
    setStationErr(null)
    setStationSheet({ mode: 'edit', station })
  }
  async function saveStation() {
    if (!stationSheet) return
    const name = stationForm.name.trim()
    if (!name) { setStationErr('El nombre es obligatorio'); return }
    const sortOrder = Number.parseInt(stationForm.sortOrder, 10)
    const branchId = stationForm.branchId.trim() || null
    setSavingStation(true); setStationErr(null)
    try {
      if (stationSheet.mode === 'create') {
        await ps.createStation({ name, sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0, branchId })
        toast('Estación creada', 'success')
      } else {
        await ps.updateStation(stationSheet.station.id, { name, sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0, branchId })
        toast('Estación actualizada', 'success')
      }
      setStationSheet(null)
      list.reload()
    } catch (e) {
      setStationErr(e instanceof Error ? e.message : String(e))
    } finally {
      setSavingStation(false)
    }
  }
  async function removeStation(station: ps.Station) {
    const ok = await confirm({
      title: `Eliminar “${station.name}”`,
      message: station.outputs.length > 0
        ? `Esta estación tiene ${station.outputs.length} salida(s). Se eliminarán junto con la estación. Esta acción no se puede deshacer.`
        : 'Esta acción no se puede deshacer.',
      tone: 'danger',
      confirmLabel: 'Eliminar',
    })
    if (!ok) return
    try {
      await ps.deleteStation(station.id)
      toast('Estación eliminada', 'success')
      if (outputsForId === station.id) setOutputsForId(null)
      list.reload()
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'danger')
    }
  }

  // ── salidas: handlers ──────────────────────────────────────────────────────
  function openCreateOutput() {
    setOutputForm(emptyOutputForm())
    setOutputErr(null)
    setOutputSheet({ mode: 'create' })
  }
  function openEditOutput(output: ps.StationOutput) {
    setOutputForm({
      type: (output.type === 'KDS' ? 'KDS' : 'PRINTER'),
      name: output.name,
      paperWidth: (output.paperWidth ?? '80mm') as ps.PaperWidth,
      templateKind: (output.templateKind ?? 'KITCHEN-thermal') as ps.TemplateKind,
      enabled: output.enabled,
    })
    setOutputErr(null)
    setOutputSheet({ mode: 'edit', output })
  }
  async function saveOutput() {
    if (!outputSheet || !outputsForId) return
    const name = outputForm.name.trim()
    if (!name) { setOutputErr('El nombre es obligatorio'); return }
    setSavingOutput(true); setOutputErr(null)
    try {
      if (outputSheet.mode === 'create') {
        await ps.createOutput(outputsForId, {
          type: outputForm.type,
          name,
          ...(outputForm.type === 'PRINTER' ? { paperWidth: outputForm.paperWidth, templateKind: outputForm.templateKind } : {}),
          enabled: outputForm.enabled,
        })
        toast('Salida agregada', 'success')
      } else {
        await ps.updateOutput(outputSheet.output.id, {
          type: outputForm.type,
          name,
          ...(outputForm.type === 'PRINTER' ? { paperWidth: outputForm.paperWidth, templateKind: outputForm.templateKind } : {}),
          enabled: outputForm.enabled,
        })
        toast('Salida actualizada', 'success')
      }
      setOutputSheet(null)
      list.reload()
    } catch (e) {
      setOutputErr(e instanceof Error ? e.message : String(e))
    } finally {
      setSavingOutput(false)
    }
  }
  async function removeOutput(output: ps.StationOutput) {
    const ok = await confirm({
      title: `Eliminar salida “${output.name}”`,
      message: 'Esta acción no se puede deshacer.',
      tone: 'danger',
      confirmLabel: 'Eliminar',
    })
    if (!ok) return
    try {
      await ps.deleteOutput(output.id)
      toast('Salida eliminada', 'success')
      list.reload()
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'danger')
    }
  }

  // ── tabla ───────────────────────────────────────────────────────────────────
  const columns: DataTableColumn<ps.Station>[] = [
    {
      key: 'name',
      header: 'Estación',
      render: (s) => (
        <div>
          <div className="adm-row__title">{s.name}</div>
          <div className="adm-row__meta">orden {s.sortOrder}{s.env ? ` · ${s.env}` : ''}</div>
        </div>
      ),
    },
    {
      key: 'branch',
      header: 'Sucursal',
      render: (s) => (
        <span style={{ color: s.branchId ? 'var(--pos-fg)' : 'var(--pos-fg-tertiary)' }}>
          {s.branchId ?? 'Todas las sucursales'}
        </span>
      ),
    },
    {
      key: 'outputs',
      header: 'Salidas',
      align: 'right',
      width: '90px',
      render: (s) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{s.outputs.length}</span>,
    },
    {
      key: 'status',
      header: 'Estado',
      width: '160px',
      render: (s) => { const st = pairingStatus(s); return <StatusPill tone={st.tone} label={st.label} /> },
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '150px',
      render: (s) => (
        <div style={{ display: 'inline-flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => setOutputsForId(s.id)} aria-label="Salidas" title="Gestionar salidas"><Sliders size={15} /></button>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => openEditStation(s)} aria-label="Editar" title="Editar estación"><Pencil size={15} /></button>
          <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => removeStation(s)} aria-label="Eliminar" title="Eliminar estación"><Trash2 size={15} /></button>
        </div>
      ),
    },
  ]

  const isPrinterForm = outputForm.type === 'PRINTER'

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <h1 className="adm-page__title">Estaciones de impresión</h1>
          <p className="adm-page__sub">Estaciones lógicas (Cocina, Barra…) con sus salidas KDS / impresora. Las impresoras físicas las administra el Bridge local.</p>
        </div>
        <div className="adm-page__actions">
          <button className="adm-btn adm-btn--primary" onClick={openCreateStation}><Plus size={16} /> Nueva estación</button>
        </div>
      </div>

      <RelayPairingCard />

      {asyncState(list.loading, list.error, list.reload)}
      {list.data && (
        <DataTable
          columns={columns}
          rows={list.data}
          getRowKey={(s) => s.id}
          emptyText="Sin estaciones. Crea la primera (p. ej. “Cocina”)."
        />
      )}

      {/* Estación: crear / editar */}
      <SectionedSheet
        open={stationSheet != null}
        title={stationSheet?.mode === 'edit' ? 'Editar estación' : 'Nueva estación'}
        onClose={() => setStationSheet(null)}
        footer={
          <>
            <button className="adm-btn" onClick={() => setStationSheet(null)}>Cancelar</button>
            <button className="adm-btn adm-btn--primary" onClick={saveStation} disabled={savingStation || !stationForm.name.trim()}>
              {savingStation ? 'Guardando…' : 'Guardar'}
            </button>
          </>
        }
      >
        <FormSection title="Datos de la estación">
          {stationErr && <div className="adm-banner adm-banner--danger" role="alert">{stationErr}</div>}
          <label className="adm-field">
            <span className="adm-field__label">Nombre</span>
            <input className="adm-input" value={stationForm.name} onChange={(e) => setStationForm({ ...stationForm, name: e.target.value })} placeholder="Cocina" autoFocus />
          </label>
          <label className="adm-field">
            <span className="adm-field__label">Orden</span>
            <input className="adm-input" type="number" min={0} value={stationForm.sortOrder} onChange={(e) => setStationForm({ ...stationForm, sortOrder: e.target.value })} />
            <small style={{ color: 'var(--pos-fg-tertiary)' }}>Orden de aparición (menor primero).</small>
          </label>
          <label className="adm-field">
            <span className="adm-field__label">Sucursal (ID, opcional)</span>
            <input className="adm-input" value={stationForm.branchId} onChange={(e) => setStationForm({ ...stationForm, branchId: e.target.value })} placeholder="Dejar vacío = todas las sucursales" />
            <small style={{ color: 'var(--pos-fg-tertiary)' }}>UUID de la sucursal. Vacío = estación compartida por todas.</small>
          </label>
        </FormSection>
      </SectionedSheet>

      {/* Salidas de la estación */}
      <SectionedSheet
        open={outputsForId != null}
        title={outputsStation ? `Salidas · ${outputsStation.name}` : 'Salidas'}
        onClose={() => setOutputsForId(null)}
        footer={<button className="adm-btn" onClick={() => setOutputsForId(null)}>Cerrar</button>}
      >
        <FormSection title="Salidas configuradas">
          <div className="adm-page__actions" style={{ marginBottom: 4 }}>
            <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={openCreateOutput}><Plus size={15} /> Agregar salida</button>
          </div>
          <div className="adm-card adm-card--pad0">
            <div className="adm-list">
              {(!outputsStation || outputsStation.outputs.length === 0) && (
                <div className="adm-empty">Sin salidas. Agrega una KDS o impresora.</div>
              )}
              {outputsStation?.outputs.map((o) => (
                <div key={o.id} className="adm-row">
                  <span style={{ color: 'var(--pos-fg-tertiary)' }}>
                    {o.type === 'KDS' ? <Radio size={18} /> : <Sliders size={18} />}
                  </span>
                  <div className="adm-row__main">
                    <div className="adm-row__title">
                      {o.name} {!o.enabled && <span className="adm-badge">deshabilitada</span>}
                    </div>
                    <div className="adm-row__meta">
                      {OUTPUT_TYPE_LABEL[o.type] ?? o.type}
                      {o.type === 'PRINTER' && o.paperWidth ? ` · ${PAPER_LABEL[o.paperWidth] ?? o.paperWidth}` : ''}
                      {o.type === 'PRINTER' && o.templateKind ? ` · ${TEMPLATE_LABEL[o.templateKind] ?? o.templateKind}` : ''}
                    </div>
                  </div>
                  <div className="adm-row__tail">
                    <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => openEditOutput(o)} aria-label="Editar salida" title="Editar"><Pencil size={15} /></button>
                    <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => removeOutput(o)} aria-label="Eliminar salida" title="Eliminar"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </FormSection>
      </SectionedSheet>

      {/* Salida: crear / editar */}
      <SectionedSheet
        open={outputSheet != null}
        title={outputSheet?.mode === 'edit' ? 'Editar salida' : 'Nueva salida'}
        onClose={() => setOutputSheet(null)}
        footer={
          <>
            <button className="adm-btn" onClick={() => setOutputSheet(null)}>Cancelar</button>
            <button className="adm-btn adm-btn--primary" onClick={saveOutput} disabled={savingOutput || !outputForm.name.trim()}>
              {savingOutput ? 'Guardando…' : 'Guardar'}
            </button>
          </>
        }
      >
        <FormSection title="Tipo de salida">
          {outputErr && <div className="adm-banner adm-banner--danger" role="alert">{outputErr}</div>}
          <label className="adm-field">
            <span className="adm-field__label">Tipo</span>
            <select className="adm-select" value={outputForm.type} onChange={(e) => setOutputForm({ ...outputForm, type: e.target.value as ps.OutputType })} aria-label="Tipo de salida">
              <option value="PRINTER">Impresora (papel)</option>
              <option value="KDS">KDS (pantalla de cocina)</option>
            </select>
          </label>
          <label className="adm-field">
            <span className="adm-field__label">Nombre</span>
            <input className="adm-input" value={outputForm.name} onChange={(e) => setOutputForm({ ...outputForm, name: e.target.value })} placeholder={isPrinterForm ? 'Comandera cocina' : 'Pantalla cocina'} autoFocus />
          </label>
        </FormSection>

        {isPrinterForm && (
          <FormSection title="Impresora">
            <label className="adm-field">
              <span className="adm-field__label">Ancho de papel</span>
              <select className="adm-select" value={outputForm.paperWidth} onChange={(e) => setOutputForm({ ...outputForm, paperWidth: e.target.value as ps.PaperWidth })} aria-label="Ancho de papel">
                <option value="58mm">58 mm</option>
                <option value="80mm">80 mm</option>
                <option value="A4">A4</option>
              </select>
            </label>
            <label className="adm-field">
              <span className="adm-field__label">Plantilla</span>
              <select className="adm-select" value={outputForm.templateKind} onChange={(e) => setOutputForm({ ...outputForm, templateKind: e.target.value as ps.TemplateKind })} aria-label="Plantilla">
                <option value="KITCHEN-thermal">Comanda (cocina)</option>
                <option value="RECEIPT-thermal">Ticket (recibo)</option>
                <option value="SLIP-thermal">Vale (slip)</option>
              </select>
            </label>
          </FormSection>
        )}

        <FormSection title="Estado">
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={outputForm.enabled} onChange={(e) => setOutputForm({ ...outputForm, enabled: e.target.checked })} /> Habilitada
          </label>
        </FormSection>
      </SectionedSheet>
    </div>
  )
}

// ── Relay: emparejamiento por código ─────────────────────────────────────────
// El dispositivo adicional (o el admin) teclea el código de emparejamiento que
// muestra el Bridge local → resuelve a {stationId, name} (acotado a tenant+env
// del JWT). Solo lectura: confirma que el código está vinculado a una estación.
function RelayPairingCard() {
  const { toast } = useToast()
  const [code, setCode] = useState('')
  const [resolving, setResolving] = useState(false)
  const [result, setResult] = useState<ps.ResolvedStation | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function resolve() {
    const trimmed = code.trim()
    if (!trimmed) { setError('Ingresa un código'); return }
    setResolving(true); setError(null); setResult(null)
    try {
      const r = await ps.resolvePairingCode(trimmed)
      setResult(r)
      toast(`Código vinculado a “${r.name}”`, 'success')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setResolving(false)
    }
  }

  return (
    <div className="adm-card" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Link2 size={16} style={{ color: 'var(--pos-fg-tertiary)' }} />
        <strong style={{ fontSize: 'var(--pos-text-sm)' }}>Emparejar dispositivo</strong>
      </div>
      <p className="adm-row__meta" style={{ marginBottom: 10 }}>
        Teclea el código de emparejamiento que muestra el Bridge para confirmar a qué estación queda vinculado este dispositivo.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label className="adm-field" style={{ flex: '1 1 220px', minWidth: 0 }}>
          <span className="adm-field__label">Código de emparejamiento</span>
          <input
            className="adm-input"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(null) }}
            onKeyDown={(e) => { if (e.key === 'Enter') resolve() }}
            placeholder="XXXX-XXXX"
            aria-label="Código de emparejamiento"
          />
        </label>
        <button className="adm-btn adm-btn--primary" onClick={resolve} disabled={resolving || !code.trim()}>
          {resolving ? 'Verificando…' : 'Verificar'}
        </button>
      </div>
      {error && <div className="adm-banner adm-banner--danger" role="alert" style={{ marginTop: 10 }}>{error}</div>}
      {result && (
        <div className="adm-banner" role="status" style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          <StatusPill tone="ok" label="Vinculado" />
          <span>Estación: <strong>{result.name}</strong></span>
        </div>
      )}
    </div>
  )
}
